import { AppState, StorageData, Wire } from '../types';
import { logger } from '../utils/logger';

export class StorageManager {
  private static readonly STORAGE_KEY = 'kiwiHarnessData';
  private static readonly APP_VERSION = '2.0';

  static save(state: AppState): void {
    try {
      const data: StorageData = {
        ...state,
        lastModified: Date.now(),
        appVersion: this.APP_VERSION
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      logger.storage.error('Failed to save to localStorage', error);
      // Handle quota exceeded or other storage errors
    }
  }

  static load(): AppState | null {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (!saved) {
        return null;
      }

      const data: StorageData = JSON.parse(saved);
      
      // Validate data structure
      if (!this.isValidData(data)) {
        logger.storage.warn('Invalid saved data structure');
        return null;
      }

      return this.migrate(data);
    } catch (error) {
      logger.storage.error('Failed to load from localStorage', error);
      return null;
    }
  }

  /**
   * Normalizes stored data to the current {@link AppState} shape: maps legacy
   * "terminal" field names to "connector", and guarantees every collection
   * exists. Electrical defaults and segment lengths are applied downstream when
   * the state is loaded into the {@link StateManager}.
   */
  private static migrate(raw: any): AppState {
    const connectors = raw.connectors ?? raw.terminals ?? [];
    const wires: Wire[] = (raw.wires ?? []).map((w: any) => ({
      ...w,
      startConnectorId: w.startConnectorId ?? w.startTerminalId,
      endConnectorId: w.endConnectorId ?? w.endTerminalId,
      path: w.path ?? []
    }));

    return {
      connectors,
      wires,
      nodes: raw.nodes ?? [],
      connectorTypes: raw.connectorTypes ?? [],
      segments: raw.segments ?? [],
      background: raw.background
    };
  }

  static clear(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      logger.storage.error('Failed to clear localStorage', error);
    }
  }

  static export(state: AppState): void {
    try {
      const data = {
        ...state,
        exportDate: new Date().toISOString(),
        appVersion: this.APP_VERSION
      };
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { 
        type: 'application/json' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kiwi-harness-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      logger.storage.error('Failed to export data', error);
    }
  }

  static import(file: File): Promise<AppState> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target?.result as string);
          
          if (!this.isValidData(data)) {
            reject(new Error('Invalid file format'));
            return;
          }

          resolve(this.migrate(data));
        } catch (error) {
          reject(new Error('Failed to parse file: ' + (error as Error).message));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsText(file);
    });
  }

  private static isValidData(data: any): data is StorageData {
    return (
      data &&
      typeof data === 'object' &&
      (Array.isArray(data.connectors) || Array.isArray(data.terminals)) &&
      Array.isArray(data.nodes) &&
      Array.isArray(data.wires)
    );
  }

  static clearWithConfirmation(): boolean {
    if (window.confirm('Are you sure? This will delete all connectors, wires, and nodes permanently.')) {
      this.clear();
      return true;
    }
    return false;
  }

  static isStorageAvailable(): boolean {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }
}